import {
  Button,
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { validateEmail } from "../../validation";

interface ForgotPasswordStepProps {
  email: string;
  onEmailChange: (value: string) => void;
  onSubmit: (email: string) => void;
  onBackToSignIn: () => void;
  error?: string;
}

export default function ForgotPasswordStep({
  email,
  onEmailChange,
  onSubmit,
  onBackToSignIn,
  error,
}: ForgotPasswordStepProps) {
  const intl = useIntl();
  const [isCritical, setIsCritical] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateEmail(email);
    if (!validation.valid) {
      setIsCritical(true);
      return;
    }
    onSubmit(email);
  };

  const handleEmailChange = (value: string) => {
    setIsCritical(false);
    onEmailChange(value);
  };

  return (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
      <InputField
        id="email"
        name="email"
        label={
          <Typography kind="label" htmlFor="email">
            <FormattedMessage
              id="1wGZeM"
              defaultMessage="Email Address"
              description="Email field label"
            />
          </Typography>
        }
        type="text"
        value={email}
        setValue={handleEmailChange}
        placeholder={intl.formatMessage({
          id: "4oYAh7",
          defaultMessage: "Enter your email address",
          description: "Email field placeholder",
        })}
        isCritical={isCritical}
        setIsCritical={setIsCritical}
        CriticalHelperText={
          <InlineMessage
            text={intl.formatMessage({
              id: "SFmVmq",
              defaultMessage: "Please enter a valid email address",
              description: "Invalid email error message",
            })}
            colorTheme="critical"
            size="small"
            leftIcon={<Icon kind="AlertCriticalFilled" />}
          />
        }
      />
      {error && (
        <InlineMessage
          text={error}
          colorTheme="critical"
          kind="background"
          size="small"
          leftIcon={<Icon kind="AlertCriticalFilled" />}
        />
      )}
      <Button
        type="submit"
        size="large"
        className="w-full justify-center"
        dataTestId="login-forgotPassword-submit-btn"
      >
        <FormattedMessage
          id="3s73DJ"
          defaultMessage="Continue"
          description="Continue button text"
        />
      </Button>
      <Button
        kind="ghost"
        className="w-full justify-center"
        onClick={onBackToSignIn}
        dataTestId="login-backToLogin-btn"
      >
        <FormattedMessage
          id="uI4eL+"
          defaultMessage="Back to Login"
          description="Back to login button text"
        />
      </Button>
    </form>
  );
}
